import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function FestivalExtensionPaid() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading || user === null) {
    return <div className="min-h-screen flex items-center justify-center text-[#A3A3A3]"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (!user) {
    return <Navigate to={`/login?as=organiser&next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  return <ExtensionPaidStatus />;
}

function ExtensionPaidStatus() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sid = params.get("session_id");
  const [data, setData] = useState(null);
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!sid) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const r = await api.get(`/festival/extension-payment/${sid}`);
        if (stop) return;
        setData(r.data);
        if (r.data.payment_status !== "paid" && r.data.payment_status !== "expired" && tries < 15) {
          setTimeout(() => setTries((t) => t + 1), 2000);
        }
      } catch { if (!stop) setData({ payment_status: "error" }); }
    };
    tick();
    return () => { stop = true; };
  }, [sid, tries]);

  const paid = data?.payment_status === "paid";
  const failed = !sid || ["error", "expired"].includes(data?.payment_status) || (!paid && tries >= 15);
  const endLabel = data?.end_at ? new Date(data.end_at).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" }) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4" data-testid="extension-paid-page">
      <div className="card-surface p-8 max-w-md w-full text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-10 h-10 text-[#CCFF00] mx-auto mb-3" />
            <h1 className="h-display text-2xl mb-2">Payment received</h1>
            <p className="text-sm text-[#A3A3A3]" data-testid="extension-paid-message">
              {data.festival_name ? `${data.festival_name} ` : "Your festival "}now ends <span className="text-white">{endLabel}</span>.
            </p>
          </>
        ) : failed ? (
          <>
            <AlertTriangle className="w-10 h-10 text-[#FFB020] mx-auto mb-3" />
            <h1 className="h-display text-2xl mb-2">Payment not confirmed</h1>
            <p className="text-sm text-[#A3A3A3]" data-testid="extension-paid-message">We couldn't confirm this payment yet. If you were charged, it will appear shortly — or contact Rally-Lab for a new link.</p>
          </>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-[#CCFF00] mx-auto mb-3 animate-spin" />
            <h1 className="h-display text-2xl mb-2">Confirming payment…</h1>
          </>
        )}
        {data?.festival_id && (
          <button type="button" onClick={() => navigate(`/festival/${data.festival_id}`)} data-testid="extension-paid-festival-link"
            className="mt-6 px-5 py-2.5 text-xs uppercase tracking-wider font-bold bg-[#CCFF00] text-black hover:opacity-90">
            Go to festival
          </button>
        )}
      </div>
    </div>
  );
}
